import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  PaymentElement as StripePaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { StripePaymentElementOptions } from '@stripe/stripe-js';

import { NextAction, PaymentElementResponse } from '../../../@types';
import { StripePaymentMethodsTypes } from '../../../constants';
import { useNextActionHandler } from '../../../handlers/nextActionHandler';
import { queryPaymentMethodHash } from '../../../queries/methodHash';
import { useCommonOptionsStore } from '../../../store/useCommonOptionsStore';
import { stripePaymentElementPaymentMethodHashPayloadFactory } from '../../../factories/query-payment-method-hash-payload/stripePaymentElementPaymentMethodHashPayloadFactory';
import { useTranslations } from '../../../translations/useTranslations';

import { ExtendedPaymentMethodsActions } from './events';
import { StripePaymentFormProps } from './paymentElement.types';

import type { StripePaymentElementChangeEvent } from '@stripe/stripe-js';

const paymentElementOptions: StripePaymentElementOptions = {
  layout: {
    type: 'accordion',
    defaultCollapsed: true,
    radios: true,
    spacedAccordionItems: true,
  },
};

export const StripePaymentForm = forwardRef(
  (props: StripePaymentFormProps, fwdRef: any) => {
    const { onSubmit, onPaymentMethodChange, onError } = props;

    const stripe = useStripe();
    const elements = useElements();

    const lang = useCommonOptionsStore(state => state.lang);
    const propertyId = useCommonOptionsStore(state => state.propertyId);
    const apiKey = useCommonOptionsStore(state => state.apiKey);
    const apiUrl = useCommonOptionsStore(state => state.apiUrl);

    const [t] = useTranslations(lang);

    const [activeAccordionItem, setActiveAccordionItem] = useState<string>();

    const nextActionHandler = useNextActionHandler();

    // TODO: not sure if callback is needed here
    const handleSubmit = useCallback(async () => {
      if (!stripe || !elements) {
        return null;
      }

      // First submit the Elements instance to validate the inputs
      const { error: submitError } = await elements.submit();
      if (submitError) {
        // const errMessage =
        //   submitError.message || 'An unexpected error occurred.';
        if (onError) onError({ global: t('paymentMethodError') });
        window.console.error('Error submitting elements:', submitError);
        return null;
      }

      const { error: tokenError, confirmationToken } =
        await stripe.createConfirmationToken({
          elements,
        });

      if (tokenError) {
        // This point is only reached if there's an immediate error when creating the ConfirmationToken.
        // Show the error to your customer (for example, payment details incomplete)
        if (onError) onError({ global: t('paymentMethodError') });
        return null;
      }

      // Get payment method hash
      const { hash: paymentMethodHash, error: hashError } =
        await queryPaymentMethodHash(
          +propertyId,
          apiKey,
          stripePaymentElementPaymentMethodHashPayloadFactory(
            confirmationToken.id,
            props.paymentMethodConfiguration,
          ),
          apiUrl,
        );
      if (hashError) {
        if (onError) onError({ global: t('paymentMethodError') });
        return null;
      }

      if (onSubmit) {
        const responseData: PaymentElementResponse = {
          paymentMethod: activeAccordionItem as StripePaymentMethodsTypes,
          paymentMethodHash,
          confirmationToken,
        };
        const nextAction: NextAction = await onSubmit({
          ...responseData,
        });
        await nextActionHandler(nextAction, async (secret: string) => {
          return stripe.handleNextAction({ clientSecret: secret });
        });
      }

      return confirmationToken;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stripe, elements, activeAccordionItem]);

    const onChangeHandler = (event: StripePaymentElementChangeEvent) => {
      if (!event.collapsed && event.value?.type) {
        setActiveAccordionItem(event.value.type);
        if (onPaymentMethodChange) {
          onPaymentMethodChange(event.value.type);
        }
      }
      if (event.collapsed && activeAccordionItem === event.value?.type) {
        setActiveAccordionItem(undefined);
      }
    };

    useEffect(() => {
      const handleCollapse = () => {
        if (!stripe || !elements) {
          return;
        }
        const paymentElementRef = elements.getElement('payment');
        paymentElementRef.collapse();
      };
      window.addEventListener(
        ExtendedPaymentMethodsActions.Collapse,
        handleCollapse,
      );

      return () => {
        window.removeEventListener(
          ExtendedPaymentMethodsActions.Collapse,
          handleCollapse,
        );
      };
    }, [elements, stripe]);

    useImperativeHandle(fwdRef, () => ({
      async submit() {
        return handleSubmit();
      },
      // we don't want to call validate on the StripePaymentElement level
      // as the validation is handled by Stripe Elements internally via the submit call
      // and must be called directly before creating the confirmation token
      // so we return true here to indicate the form is valid
      async validate() {
        return true;
      },
    }));

    return (
      <>
        <StripePaymentElement
          id="stripe-payment-element"
          options={paymentElementOptions}
          onChange={onChangeHandler}
        />
      </>
    );
  },
);
